import { createContext, useContext, useState, useCallback, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { isValidDuration, resolveAudioDuration } from '../utils/audioDuration'

interface AudioContextType {
  currentlyPlaying: string | null
  setCurrentlyPlaying: (id: string | null) => void
  getAudioState: (id: string) => AudioState
  togglePlay: (id: string, src: string) => void
  setProgress: (id: string, progress: number) => void
  skipForward: (id: string, seconds: number) => void
  skipBackward: (id: string, seconds: number) => void
  pauseAudio: (id: string) => void
  stopAllAudio: () => void
}

interface AudioState {
  isPlaying: boolean
  progress: number
  duration: number
  currentTime: number
  error: string | null
}

const DEFAULT_AUDIO_STATE: AudioState = {
  isPlaying: false,
  progress: 0,
  duration: 0,
  currentTime: 0,
  error: null
}

const AudioContext = createContext<AudioContextType | undefined>(undefined)

export function AudioProvider({ children }: { children: React.ReactNode }) {
  const [currentlyPlaying, setCurrentlyPlaying] = useState<string | null>(null)
  const [audioStates, setAudioStates] = useState<Map<string, AudioState>>(new Map())
  const [audioElements] = useState<Map<string, HTMLAudioElement>>(new Map())
  const location = useLocation()

  const stopAllAudio = useCallback(() => {
    audioElements.forEach((audio) => {
      audio.pause()
      audio.currentTime = 0
    })
    setCurrentlyPlaying(null)
    setAudioStates((prev) => {
      const newStates = new Map(prev)
      newStates.forEach((state, id) => {
        newStates.set(id, {
          ...state,
          isPlaying: false,
          progress: 0,
          currentTime: 0
        })
      })
      return newStates
    })
  }, [audioElements])

  // Stop all audio when location changes
  useEffect(() => {
    stopAllAudio()
  }, [location.pathname, stopAllAudio])

  const getAudioState = useCallback(
    (id: string): AudioState => {
      return audioStates.get(id) || DEFAULT_AUDIO_STATE
    },
    [audioStates]
  )

  const updateDuration = useCallback((id: string, duration: number) => {
    if (!isValidDuration(duration)) return
    setAudioStates((prev) => {
      const newStates = new Map(prev)
      const currentState = newStates.get(id) || DEFAULT_AUDIO_STATE
      if (currentState.duration === duration) return prev
      newStates.set(id, {
        ...currentState,
        duration,
        error: null
      })
      return newStates
    })
  }, [])

  const syncDuration = useCallback(
    async (id: string, audioElement: HTMLAudioElement) => {
      const duration = await resolveAudioDuration(audioElement)
      updateDuration(id, duration)
    },
    [updateDuration]
  )

  const togglePlay = useCallback(
    async (id: string, src: string) => {
      let audioElement = audioElements.get(id)

      // Stop any currently playing audio first (except the one we're trying to play)
      if (currentlyPlaying && currentlyPlaying !== id) {
        const otherAudio = audioElements.get(currentlyPlaying)
        if (otherAudio) {
          otherAudio.pause()
          setAudioStates((prev) => {
            const newStates = new Map(prev)
            const otherState = getAudioState(currentlyPlaying)
            newStates.set(currentlyPlaying, { ...otherState, isPlaying: false })
            return newStates
          })
        }
      }

      // Use the src directly since components pass full Cloudinary URLs
      const audioUrl = src

      if (!audioElement) {
        audioElement = new Audio()
        audioElements.set(id, audioElement)

        const handleLoadedMetadata = () => {
          void syncDuration(id, audioElement!)
        }

        const handleDurationChange = () => {
          if (audioElement && isValidDuration(audioElement.duration)) {
            updateDuration(id, audioElement.duration)
          }
        }

        const handleError = (e: ErrorEvent) => {
          console.error('Audio error:', e)
          console.error('Audio element src:', audioElement?.src)
          console.error('Audio element error code:', audioElement?.error?.code)
          console.error('Audio element error message:', audioElement?.error?.message)

          // Try to fetch the URL to see if it exists
          if (audioElement?.src) {
            fetch(audioElement.src, { method: 'HEAD' })
              .then((response) => {
                console.error('Audio URL fetch response:', response.status, response.statusText)
              })
              .catch((error) => {
                console.error('Audio URL fetch error:', error)
              })
          }
          setAudioStates((prev) => {
            const newStates = new Map(prev)
            const currentState = newStates.get(id) || DEFAULT_AUDIO_STATE
            newStates.set(id, {
              ...currentState,
              error: 'Failed to load audio',
              isPlaying: false
            })
            return newStates
          })
          setCurrentlyPlaying(null)
        }

        const handleEnded = () => {
          setAudioStates((prev) => {
            const newStates = new Map(prev)
            const currentState = newStates.get(id) || DEFAULT_AUDIO_STATE
            newStates.set(id, {
              ...currentState,
              isPlaying: false,
              progress: 0,
              currentTime: 0
            })
            return newStates
          })
          setCurrentlyPlaying(null)
        }

        const handleTimeUpdate = () => {
          if (!audioElement) return

          if (isValidDuration(audioElement.duration)) {
            const progress = (audioElement.currentTime / audioElement.duration) * 100
            setAudioStates((prev) => {
              const newStates = new Map(prev)
              const currentState = newStates.get(id) || DEFAULT_AUDIO_STATE
              newStates.set(id, {
                ...currentState,
                progress,
                currentTime: audioElement.currentTime || 0,
                duration: audioElement.duration
              })
              return newStates
            })
            return
          }

          setAudioStates((prev) => {
            const newStates = new Map(prev)
            const currentState = newStates.get(id) || DEFAULT_AUDIO_STATE
            newStates.set(id, {
              ...currentState,
              currentTime: audioElement.currentTime || 0
            })
            return newStates
          })
        }

        audioElement.addEventListener('loadedmetadata', handleLoadedMetadata)
        audioElement.addEventListener('durationchange', handleDurationChange)
        audioElement.addEventListener('error', handleError)
        audioElement.addEventListener('ended', handleEnded)
        audioElement.addEventListener('timeupdate', handleTimeUpdate)
      }

      // Replace source and reset timeline state when URL changes (e.g. new recording)
      if (!audioElement.src || audioElement.src !== audioUrl) {
        audioElement.pause()
        audioElement.src = audioUrl
        audioElement.load()
        setAudioStates((prev) => {
          const newStates = new Map(prev)
          newStates.set(id, { ...DEFAULT_AUDIO_STATE })
          return newStates
        })
      }

      const ensureDuration = async () => {
        if (!isValidDuration(audioElement!.duration)) {
          await syncDuration(id, audioElement!)
        }
      }

      if (currentlyPlaying === id) {
        // Toggle play/pause for the same audio
        if (audioElement.paused) {
          try {
            await ensureDuration()
            await audioElement.play()
            setCurrentlyPlaying(id)
            setAudioStates((prev) => {
              const newStates = new Map(prev)
              const currentState = newStates.get(id) || DEFAULT_AUDIO_STATE
              newStates.set(id, { ...currentState, isPlaying: true, error: null })
              return newStates
            })
          } catch (error) {
            console.error('Failed to play audio:', error)
            setAudioStates((prev) => {
              const newStates = new Map(prev)
              const currentState = newStates.get(id) || DEFAULT_AUDIO_STATE
              newStates.set(id, {
                ...currentState,
                error: 'Failed to play audio',
                isPlaying: false
              })
              return newStates
            })
          }
        } else {
          audioElement.pause()
          // Don't set currentlyPlaying to null on pause - keep track of which audio is paused
          setAudioStates((prev) => {
            const newStates = new Map(prev)
            const currentState = newStates.get(id) || DEFAULT_AUDIO_STATE
            newStates.set(id, { ...currentState, isPlaying: false })
            return newStates
          })
        }
      } else {
        // Play new audio
        try {
          await ensureDuration()
          await audioElement.play()
          setCurrentlyPlaying(id)
          setAudioStates((prev) => {
            const newStates = new Map(prev)
            const currentState = newStates.get(id) || DEFAULT_AUDIO_STATE
            newStates.set(id, { ...currentState, isPlaying: true, error: null })
            return newStates
          })
        } catch (error) {
          console.error('Failed to play audio:', error)
          setAudioStates((prev) => {
            const newStates = new Map(prev)
            const currentState = newStates.get(id) || DEFAULT_AUDIO_STATE
            newStates.set(id, { ...currentState, error: 'Failed to play audio', isPlaying: false })
            return newStates
          })
        }
      }
    },
    [audioElements, currentlyPlaying, getAudioState, syncDuration, updateDuration]
  )

  const setProgress = useCallback(
    (id: string, progress: number) => {
      const audioElement = audioElements.get(id)
      if (audioElement && isValidDuration(audioElement.duration)) {
        const newTime = (progress / 100) * audioElement.duration
        audioElement.currentTime = newTime
        setAudioStates((prev) => {
          const newStates = new Map(prev)
          const currentState = newStates.get(id) || DEFAULT_AUDIO_STATE
          newStates.set(id, {
            ...currentState,
            progress,
            currentTime: newTime
          })
          return newStates
        })
      }
    },
    [audioElements]
  )

  const skipForward = useCallback(
    (id: string, seconds: number) => {
      const audioElement = audioElements.get(id)
      if (audioElement && isValidDuration(audioElement.duration)) {
        const newTime = Math.min(audioElement.currentTime + seconds, audioElement.duration)
        audioElement.currentTime = newTime
        const progress = (newTime / audioElement.duration) * 100
        setAudioStates((prev) => {
          const newStates = new Map(prev)
          const currentState = newStates.get(id) || DEFAULT_AUDIO_STATE
          newStates.set(id, {
            ...currentState,
            progress,
            currentTime: newTime
          })
          return newStates
        })
      }
    },
    [audioElements]
  )

  const skipBackward = useCallback(
    (id: string, seconds: number) => {
      const audioElement = audioElements.get(id)
      if (audioElement && isValidDuration(audioElement.duration)) {
        const newTime = Math.max(audioElement.currentTime - seconds, 0)
        audioElement.currentTime = newTime
        const progress = (newTime / audioElement.duration) * 100
        setAudioStates((prev) => {
          const newStates = new Map(prev)
          const currentState = newStates.get(id) || DEFAULT_AUDIO_STATE
          newStates.set(id, {
            ...currentState,
            progress,
            currentTime: newTime
          })
          return newStates
        })
      }
    },
    [audioElements]
  )

  const pauseAudio = useCallback(
    (id: string) => {
      const audioElement = audioElements.get(id)
      if (!audioElement || audioElement.paused) return

      audioElement.pause()
      setAudioStates((prev) => {
        const newStates = new Map(prev)
        const currentState = newStates.get(id) || DEFAULT_AUDIO_STATE
        newStates.set(id, { ...currentState, isPlaying: false })
        return newStates
      })
    },
    [audioElements]
  )

  return (
    <AudioContext.Provider
      value={{
        currentlyPlaying,
        setCurrentlyPlaying,
        getAudioState,
        togglePlay,
        setProgress,
        skipForward,
        skipBackward,
        pauseAudio,
        stopAllAudio
      }}
    >
      {children}
    </AudioContext.Provider>
  )
}

export function useAudio() {
  const context = useContext(AudioContext)
  if (context === undefined) {
    throw new Error('useAudio must be used within an AudioProvider')
  }
  return context
}
